import React from 'react';

/*
 * Round count badge (menu: Chat unread + AI Training Inbox to-dos; chat list: per-conversation
 * unread), with the number centred on its INK.
 *
 * CSS can only centre the font's boxes: the line box (room for accents and descenders, so
 * digits sat ~1px high) and each digit's advance width (a "1" is a thin stroke in a wide cell,
 * so "61" sat off to one side). Canvas measureText reports where the glyphs' pixels actually
 * are; the number is drawn as SVG text at the position that centres them, which SVG allows to
 * a fraction of a pixel. The badge is `size` or one pixel larger, whichever puts that baseline
 * nearest a whole device pixel, for renderers that snap the baseline. Until the font has
 * loaded (and in jsdom, which has no canvas), flex centring on the cap-height box stands in.
 * 100+ widens into a pill.
 */
const BADGE_BLUE = '#007AFF';
// Rendered glyphs land up to ~0.25px either side of where measureText puts them, depending on
// the number (Chrome snaps glyph positions to a fraction of a pixel). Measured 9/26 with
// sub-pixel antialiasing: "61" +0.26px, "33" ~0. Splitting the difference keeps both within
// about an eighth of a pixel.
const INK_X_NUDGE = -0.125;

const inkCache = new Map();
let inkCanvas = null;

function measureInk(text, font) {
  const key = `${font}|${text}`;
  if (!inkCache.has(key)) {
    inkCanvas = inkCanvas || document.createElement('canvas');
    const ctx = inkCanvas.getContext('2d');
    if (!ctx) return null;
    ctx.font = font;
    const m = ctx.measureText(text);
    inkCache.set(key, {
      left: m.actualBoundingBoxLeft,
      right: m.actualBoundingBoxRight,
      ascent: m.actualBoundingBoxAscent,
      descent: m.actualBoundingBoxDescent,
    });
  }
  return inkCache.get(key);
}

// Of the base size and one pixel larger, the one whose centred baseline lands nearest a whole
// DEVICE pixel (ties keep the base size).
const badgeHeight = (base, ink) => {
  const dpr = window.devicePixelRatio || 1;
  const miss = (size) => {
    const baseline = dpr * (size / 2 + (ink.ascent - ink.descent) / 2);
    return Math.abs(baseline - Math.round(baseline));
  };
  return miss(base + 1) + 0.05 < miss(base) ? base + 1 : base;
};

const canMeasure = typeof navigator !== 'undefined' && !/jsdom/i.test(navigator.userAgent || '');

export default function CountBadge({
  count, size = 20, fontSize = '.85rem', fontWeight = 600, pillPadding = 6, title, style,
}) {
  const ref = React.useRef(null);
  const [ink, setInk] = React.useState(null);
  const text = String(count);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!canMeasure || !el || !(count > 0)) return undefined;
    let active = true;
    const cs = window.getComputedStyle(el);
    const font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const measure = () => { if (active) setInk(measureInk(text, font)); };
    // Measuring before the web font arrives would centre the fallback font's glyphs, so load
    // it first (resolves at once when it is already loaded).
    if (!document.fonts) measure();
    else document.fonts.load(font, text).then(measure, measure);
    return () => { active = false; };
  }, [count, text, fontSize, fontWeight]);

  if (!(count > 0)) return null;
  const box = (height) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    flexShrink: 0,
    minWidth: height,
    height,
    padding: count < 100 ? 0 : `0 ${pillPadding}px`,
    // Half the height: 50% on a wider (100+) box would draw an oval.
    borderRadius: height / 2,
    fontWeight,
    fontSize,
    lineHeight: 1,
    color: 'white',
    backgroundColor: BADGE_BLUE,
    ...style,
  });

  if (!ink) {
    return (
      <span ref={ref} title={title} style={box(size)}>
        <span style={{ display: 'block', textBox: 'trim-both cap alphabetic' }}>{text}</span>
      </span>
    );
  }
  const height = badgeHeight(size, ink);
  const width = count < 100 ? height : Math.ceil(ink.left + ink.right + 2 * pillPadding);
  return (
    <span ref={ref} title={title} style={{ ...box(height), width, padding: 0 }}>
      <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }} aria-hidden="true">
        <text x={width / 2 - (ink.right - ink.left) / 2 + INK_X_NUDGE} y={height / 2 + (ink.ascent - ink.descent) / 2} fill="white">
          {text}
        </text>
      </svg>
    </span>
  );
}
